import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SlackLogo, CheckCircle, Trash, Lightning } from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * SlackWebhookSection — settings block for the owner's Slack Incoming
 * Webhook URL. Powers the Digest Slack Fallback: when the owner has
 * this set, the Monday coaching digest ALSO posts to their Slack
 * channel (in addition to email).
 *
 * Write-only from the API's perspective — we surface a redacted
 * preview + updated_at, never the raw URL.
 */
export default function SlackWebhookSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/settings/slack-webhook");
      setStatus(data);
      setShowInput(!data.configured);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load Slack settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!webhookUrl.trim().startsWith("https://hooks.slack.com/")) {
      toast.error("Paste your full Slack Incoming Webhook URL");
      return;
    }
    setSaving(true);
    try {
      await client.put("/settings/slack-webhook", {
        slack_webhook_url: webhookUrl.trim(),
      });
      toast.success("Slack webhook saved · Monday digest will now post to your channel");
      setWebhookUrl("");
      setShowInput(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Remove your Slack webhook? The Monday digest will only be emailed.")) return;
    setSaving(true);
    try {
      await client.put("/settings/slack-webhook", { slack_webhook_url: "" });
      toast.success("Slack webhook removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally {
      setSaving(false);
    }
  };

  const testFire = async () => {
    if (testing) return;
    setTesting(true);
    try {
      await client.post("/settings/slack-webhook/test");
      toast.success("Test digest posted to Slack — check your channel");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Slack test failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="card-surface p-5 text-neutral-500 text-sm" data-testid="slack-webhook-section-loading">
        Loading…
      </div>
    );
  }

  return (
    <div className="card-surface p-5 space-y-4" data-testid="slack-webhook-section">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 inline-flex items-center gap-1.5">
            <SlackLogo size={12} weight="fill" className="text-[#4A154B]" />
            SLACK · MONDAY DIGEST FALLBACK
          </div>
          <div className="text-lg font-semibold text-white">Post the Monday digest to Slack</div>
          <div className="text-xs text-neutral-400 mt-1 max-w-lg">
            Optional. When set, your weekly coaching digest posts to this
            Slack channel in addition to email. Some owners live in Slack —
            this puts the numbers where they'll actually be read.
          </div>
        </div>
        {status?.configured && (
          <div className="pill pill-emerald shrink-0 inline-flex items-center gap-1"
               data-testid="slack-webhook-configured-badge">
            <CheckCircle size={11} weight="fill" />
            Configured
          </div>
        )}
      </div>

      {status?.configured && !showInput && (
        <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs text-neutral-300 font-mono truncate" data-testid="slack-webhook-preview">
              {status.preview}
            </div>
            {status.updated_at && (
              <div className="text-[10px] text-neutral-500 mt-0.5">
                Updated {new Date(status.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </div>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={testFire}
              disabled={testing}
              data-testid="slack-webhook-test"
              className="text-xs rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-40 px-3 py-1.5 inline-flex items-center gap-1.5"
              title="Fire a test digest to your Slack channel right now"
            >
              <Lightning size={11} weight="fill" />
              {testing ? "Sending…" : "Send test"}
            </button>
            <button
              onClick={() => setShowInput(true)}
              data-testid="slack-webhook-replace"
              className="btn-secondary rounded-full px-3 py-1.5 text-xs"
            >
              Replace
            </button>
            <button
              onClick={clear}
              disabled={saving}
              data-testid="slack-webhook-remove"
              className="text-xs rounded-full border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 px-3 py-1.5 inline-flex items-center gap-1.5"
            >
              <Trash size={11} />
              Remove
            </button>
          </div>
        </div>
      )}

      {(showInput || !status?.configured) && (
        <div className="space-y-2">
          <label className="block text-[10px] uppercase tracking-widest text-neutral-500">
            Incoming webhook URL
          </label>
          <input
            type="url"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://hooks.slack.com/services/T00000/B00000/xxxx"
            data-testid="slack-webhook-input"
            className="w-full bg-black/40 border border-white/10 rounded-lg text-white text-sm py-2.5 px-3 focus:outline-none focus:border-[#4A154B]/60 font-mono"
          />
          <div className="text-[11px] text-neutral-500 leading-relaxed">
            Get one at <b>Slack app → Incoming Webhooks → Add New Webhook to Workspace</b>.
            Pick the channel you want the digest posted to.
          </div>
          <div className="flex gap-2 pt-1">
            {status?.configured && (
              <button
                onClick={() => { setShowInput(false); setWebhookUrl(""); }}
                data-testid="slack-webhook-cancel"
                className="text-sm text-neutral-400 hover:text-white px-3 py-2"
              >
                Cancel
              </button>
            )}
            <button
              onClick={save}
              disabled={!webhookUrl.trim() || saving}
              data-testid="slack-webhook-save"
              className="text-sm rounded-md bg-[#4A154B] hover:bg-[#5c1e5f] disabled:opacity-40 text-white font-semibold px-4 py-2 inline-flex items-center gap-1.5"
            >
              <SlackLogo size={13} weight="fill" />
              {saving ? "Saving…" : "Save webhook"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
